import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/Button";
import FormTemplatesHeader from "../components/FormTemplatesHeader";
import FormTemplatesTable from "../components/FormTemplatesTable";
import ArchiveModal from "../components/TemplateArchieveModal";
import {
  getAllForms,
  getFormByUuid,
  createForm,
  deleteForm,
} from "../services/consultationform";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { userId } from "../config";
import { ConsultationTemplate } from "../types/consultation";
import { FormTemplate } from "../types/forms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FormTemplatesPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [consultations, setConsultations] = useState<ConsultationTemplate[]>(
    []
  );
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  const navigate = useNavigate();
  const templatesKey = `form_templates_${userId}`;
  const archivedKey = `archived_templates_${userId}`;
  const [formTemplates, setFormTemplates] = useLocalStorage<FormTemplate[]>(
    templatesKey,
    []
  );
  const [archivedTemplates, setArchivedTemplates] = useLocalStorage<
    FormTemplate[]
  >(archivedKey, []);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const data = await getAllForms();
        const templates = data.forms.map((form: ConsultationTemplate) => ({
          id: form.id,
          title: form.title,
          description: form.description || "",
          frequency: form.frequency || "",
          status: form.status,
          uuid: form.uuid,
        }));
        setConsultations(templates);
      } catch (err) {
        console.error("Error fetching forms:", err);
      }
    };
    fetchForms();
  }, []);

  const filtered = consultations.filter(
    (t) =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const itemsPerPageNum = parseInt(itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPageNum);
  const startIndex = (currentPage - 1) * itemsPerPageNum;
  const paginatedTemplates = filtered.slice(
    startIndex,
    startIndex + itemsPerPageNum
  );

  async function handleEditTemplate(uuid: string) {
    try {
      navigate(`/form-builder/${userId}/${uuid}`);
    } catch (err) {
      console.error(err);
    }
  }

  const handleCreateNew = async () => {
    try {
      const newFormData = {
        title: "Untitled Form",
        description: "Provide a brief description for your form.",
        services: [],
        sections: [],
        userId,
      };
      const res = await createForm(newFormData);
      const newForm = res.form;
      console.log("------------------", newForm.uuid);

      setFormTemplates((prev) => [
        ...prev,
        {
          id: String(newForm.id),
          uuid: newForm.uuid,
          title: newForm.title,
          description: newForm.description,
          services: newForm.services || [],
          sections: newForm.sections || [],
          saved: true,
          draft: newForm.status === "draft",
          userId,
          fields: [],
        },
      ]);

      navigate(`/form-builder/${userId}/${newForm.uuid}`);
    } catch (err) {
      toast.error("Failed to create new form");
    }
  };

  const handleArchiveTemplate = (templateId: string | number) => {
    const toArchive = formTemplates.find((t) => t.id === templateId);
    if (!toArchive) return;
    setFormTemplates(formTemplates.filter((t) => t.id !== templateId));
    setArchivedTemplates([...archivedTemplates, toArchive]);
    toast.info("Form archived successfully");
  };

  const handleUnarchiveTemplate = (templateId: string | number) => {
    const toUnarchive = archivedTemplates.find((t) => t.id === templateId);
    if (!toUnarchive) return;
    setArchivedTemplates(archivedTemplates.filter((t) => t.id !== templateId));
    setFormTemplates([...formTemplates, toUnarchive]);
    toast.success("Form restored successfully");
  };

  const handleDeleteTemplate = async (uuid: string) => {
    try {
      await deleteForm(uuid);
      setConsultations((prev) => prev.filter((t) => t.uuid !== uuid));
      setFormTemplates((prev) => prev.filter((t) => t.uuid !== uuid));
      setArchivedTemplates((prev) => prev.filter((t) => t.uuid !== uuid));
      toast.success("Form deleted successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete form");
    }
  };

  return (
    <div className="mx-auto max-w-8xl px-2 py-2 sm:px-4">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="mb-8 flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-[#402B69]"
        >
          <ArrowLeft size={18} />
          Back to Consultations
        </Button>
        <h1 className="text-2xl font-semibold text-[#402B69]">
          Form Templates
        </h1>
      </div>

      <FormTemplatesHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(val) => {
          setItemsPerPage(val);
          setCurrentPage(1);
        }}
        onCreateNew={handleCreateNew}
        onOpenArchiveModal={() => setIsArchiveModalOpen(true)}
      />

      <FormTemplatesTable
        templates={paginatedTemplates}
        onEditTemplate={handleEditTemplate}
        onArchiveTemplate={handleArchiveTemplate}
        onDeleteTemplate={handleDeleteTemplate}
      />

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-600">
          <div>
            Showing {startIndex + 1}–
            {Math.min(startIndex + itemsPerPageNum, filtered.length)} of{" "}
            {filtered.length} templates
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ArchiveModal
        isOpen={isArchiveModalOpen}
        archivedTemplates={archivedTemplates}
        onClose={() => setIsArchiveModalOpen(false)}
        onUnarchive={handleUnarchiveTemplate}
      />
    </div>
  );
};

export default FormTemplatesPage;
